import { styled, css } from '@vebox/etui-system';
import '../utilities/responsive.js';
import '../utilities/format-ms.js';
import '../utilities/create-transition.js';
import { delay } from '../utilities/delay.js';
import { useRef, createElement, Fragment } from 'react';
import '../utilities/get-styleprop.js';
import '../useDebounce/useDebounce.js';
import '../useWindowSize/useWindowSize.js';
import '../useResponsive/useResponsive.js';
import { _ as _objectWithoutProperties, a as _objectSpread2, b as _extends } from '../_rollupPluginBabelHelpers-3b7bd988.js';
import { Aside, Div, Ul } from '@vebox/etui-system-elements';
import { H5 } from '../H5/H5.js';
import '../Svg-0180d7b5.js';
import '../Icon/Icon.js';
import '@vebox/etui-icons';
import { icons } from '../Icon/icons.js';
import '../Icon/helper.js';
import { fade, darken } from '@vebox/etui-colors';
import { Keyframes, animated } from 'react-spring/renderprops.cjs';
import '../Badge/Badge.js';
import ClickOutside from '../ClickOutside/ClickOutside.js';
import '../Tooltip/Tooltip.js';
import '../TileButton/TileButton.js';
import HeaderNavItem from '../Header/HeaderNavItem.js';
import SideMenuItem from './SideMenuItem.js';

const _excluded = ["theme"];
const SideMenuAside = styled(Aside).attrs(_ref => {
  let {
      theme
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  return _objectSpread2({
    display: 'inline-block',
    position: 'relative',
    height: '100%',
    width: '100%',
    bg: fade(theme.palette.black[80], 0.98),
    border: `1px solid ${darken(theme.palette.black[80], 0.1)}`,
    overflow: 'hidden'
  }, props);
})`
  border-left-width: 0;
`;

const _excluded$1 = ["theme"];
const SideMenuContentDiv = styled(Div).attrs(_ref => {
  let {
      theme
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded$1);
  return _objectSpread2({
    display: 'flex',
    height: '100%',
    p: [`${theme.spacing.md}px`, `0 0 0 ${theme.spacing.xxlg}px`]
  }, props);
})``;

const _excluded$2 = ["theme"];
const SideMenuContentInnerDiv = styled(Div).attrs(_ref => {
  let {
      theme
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded$2);
  return _objectSpread2({
    mb: theme.spacing.lg,
    width: `100%`
  }, props);
})`
  overflow-x: hidden;
  overflow-y: auto;
`;

const SideMenuContentScrollDiv = styled(Div)`
  overflow: auto;
  padding-bottom: 10em;
  max-height: 100%;
  margin-right: -100px;
  padding-right: 100px;
`;

const _excluded$3 = ["theme"];
const SideMenuTitleDiv = styled(Div).attrs(_ref => {
  let {
      theme
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded$3);
  return _objectSpread2({
    display: 'flex',
    alignItems: 'center',
    height: theme.shape.headerBottomHeight,
    m: [0, `0 0 ${theme.spacing.xxlg}px 0`],
    bg: theme.palette.black[80],
    boxShadow: theme.shadows[1]
  }, props);
})``;

const SideMenuTitleH5 = styled(H5)`
  ${({
  theme
}) => css`
    color: ${theme.palette.black[40]};
    padding: 0 ${theme.spacing.md}px;
  `};
`;

const _excluded$4 = ["theme"];
const SideMenuUl = styled(Ul).attrs(_ref => {
  let {
      theme
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded$4);
  return _objectSpread2({
    display: 'flex',
    flexWrap: 'wrap',
    listStyle: 'none',
    m: `${theme.spacing.md}px 0`,
    p: 0,
    width: ['100%', '100%', 'calc(100% - 50px)']
  }, props);
})``;

const _excluded$5 = ["theme"];
const SideMenuH5 = styled(H5).attrs(_ref => {
  let {
      theme
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded$5);
  return _objectSpread2({
    mb: [theme.spacing.sm, theme.spacing.md],
    lineHeight: 1
  }, props);
})`
  color: ${({
  theme
}) => theme.palette.black[40]};
`;

const SideMenuAnimation = Keyframes.Spring({
  peek: [{
    x: 0,
    from: {
      x: -100
    },
    delay: 500
  }, {
    x: -100,
    delay: 800
  }],
  open: {
    delay: 0,
    x: 0
  },
  close: async call => {
    await delay(100);
    await call({
      delay: 0,
      x: -100
    });
  }
});
function SideMenu({
  title,
  groups,
  isSideMenuOpen,
  toggleSideMenu,
  closeSideMenu,
  testId
}) {
  const wrapperRef = useRef();
  const handleClickOutside = event => {
    const isRef = wrapperRef;
    const targetInside = wrapperRef.current.contains(event.target);
    if (isRef && !targetInside) {
      if (typeof closeSideMenu === 'function') {
        closeSideMenu(event);
      }
    }
  };
  const renderMenuItems = items => {
    return items.map(item => {
      return /*#__PURE__*/createElement(SideMenuItem, _extends({
        key: item.key,
        isSidebarMenuOpen: isSideMenuOpen,
        toggleSidebarMenu: toggleSideMenu
      }, item));
    });
  };
  const renderMenu = () => {
    const resultGroups = [...groups];
    const content = [];
    resultGroups.forEach((group, index) => {
      const items = renderMenuItems(group.items);
      content.push(/*#__PURE__*/createElement(Fragment, {
        key: index
      }, /*#__PURE__*/createElement(SideMenuH5, null, group.name), /*#__PURE__*/createElement(SideMenuUl, null, items)));
    });
    return content;
  };
  const state = isSideMenuOpen === undefined ? 'peek' : isSideMenuOpen ? 'open' : 'close';
  return (
    /*#__PURE__*/
    // @ts-ignore
    createElement(SideMenuAnimation, {
      native: true,
      state: state
    }, ({
      x
    }) => /*#__PURE__*/createElement(animated.div, {
      className: "sidebar",
      style: {
        transform: x.interpolate(cx => `translate3d(${cx}%,0,0)`),
        zIndex: 810,
        position: 'fixed',
        marginTop: -50,
        paddingBottom: 50,
        height: '100%',
        width: '100%'
      }
    }, /*#__PURE__*/createElement(ClickOutside, {
      onClickOutside: handleClickOutside,
      styles: {
        root: {
          display: 'inline-block',
          position: 'relative',
          height: '100%',
          width: ['100%', '65%', '65%', '50%']
        }
      }
    }, /*#__PURE__*/createElement(SideMenuAside, {
      elRef: wrapperRef
    }, /*#__PURE__*/createElement(SideMenuTitleDiv, null, /*#__PURE__*/createElement(HeaderNavItem, {
      title: "Menu",
      onClick: toggleSideMenu,
      iconProps: {
        glyph: icons.CLOSE
      },
      isToggle: true,
      isToggleActive: true,
      bottom: true
    }), title && /*#__PURE__*/createElement(SideMenuTitleH5, null, title)), /*#__PURE__*/createElement(SideMenuContentDiv, null, /*#__PURE__*/createElement(SideMenuContentInnerDiv, null, /*#__PURE__*/createElement(SideMenuContentScrollDiv, {
      testId: testId
    }, renderMenu())))))))
  );
}

export default SideMenu;
